/* eslint-disable implicit-arrow-linebreak */
import { Suspense, lazy } from 'react';
import { Navigate, useParams } from 'react-router';
import AuthGuard from './components/AuthGuard';
import GuestGuard from './components/GuestGuard';
import LoadingScreen from './components/LoadingScreen';
import DashboardLayout from './components/dashboard/DashboardLayout';
import HelpLayout from './components/dashboard/HelpLayout';
// MTB: /projects routes disabled — keep imports commented with the route block below
// import OrganizationProjectDetails from './components/organization/projects/OrganizationProjectDetails';
// import ProjectSection from './components/organization/projects/ProjectSection';
// import CasesViewByProjects from './components/organization/projects/cases/CasesViewByProjects';
// import ProjectFrs from './components/organization/projects/frs/FRS';
import UserSettings from './pages/user-settings/user-settings';
import BackLogCases from './pages/cases/BackLog';
import EditCases from './pages/cases/EditCases';
import UnApprovedCases from './pages/cases/UnApprovedCases';
import OpenCases from './pages/cases/openCases';

import PermissionGuard from './components/PermissionGuard';
import SubscriptionProductPermissionGuard from './mindtime/components/security/SubscriptionProductPermissionGuard';
import ViewCases from './pages/cases/ViewCases';
import ContactsListView from './pages/contacts/listView';
import Overview from './pages/dashboard/Overview';
import Contacts from './pages/help/Contacts';
import Dashboard from './pages/help/Dashboard';
import Introduction from './pages/help/Introduction';
import Project from './pages/help/Project';
import HelpCases from './pages/help/cases/HelpCases';
import HelpCreateCases from './pages/help/cases/HelpCreateCases';
import HelpViewCases from './pages/help/cases/HelpViewCases';
import ViewReporting from './pages/reporting/ViewReporting';

import {
  APPROVALS,
  CLIENT_BACKUPS,
  CLIENT_BACKUP_PORTAL,
  CLIENT_BACKUP_USERS,
  CLIENT_CASES,
  CLIENT_DASHBOARD,
  CLIENT_EXPLORER,
  CLIENT_HISTORY,
  CLIENT_LISTS,
  CLIENT_MINDTIME_SECURITY_COMPLIANCE_SETTINGS,
  CLIENT_MINDTIME_SECURITY_SSL_MANAGEMENT,
  CLIENT_ORDERS,
  CLIENT_PLANNING,
  CLIENT_PRICES,
  CLIENT_SUBSCRIPTIONS,
  // CLIENT_PROJECTS, // MTB: /projects routes disabled
  CLIENT_REPORTING,
  CLIENT_SUPPORT,
  CLIENT_USERS,
  CLIETN_COMPANIES,
  CLIETN_HOME
} from 'src/constants/constants';
import TwoFactorAuthGuard from './components/TwoFactorAuthGuard';
import { ChatsHistory } from './components/chat/ChatHistory';
import TwoFactorAuthentication from './pages/authentication/TwoFactorAuthentication';
import TestingCases from './pages/cases/TestingCases';
import { CompanyDetails } from './pages/companies';
import Companies from './pages/companies/companies';
import ProjectDetails from './pages/companies/company-details/projects/project-details/project-details';
import CreateOrder from './pages/orders/createOrder';
import Orders from './pages/orders/orders';
import ViewOrder from './pages/orders/viewOrder';
import Subscriptions from './pages/subscriptions/subscriptions';
import CreateSubscription from './pages/subscriptions/createSubscription';
import SubscriptionDetails from './pages/subscriptions/subscriptionDetails';
import Planning from './pages/planning/planning';
import UnderDevelopment from './pages/under-development/UnderDevelopment';

// MindTime page imports
import MindtimeDashboard from './mindtime/pages/dashboard/MindtimeDashboard';
import MindtimeHistory from './mindtime/pages/history/History';
import MindtimeLists from './mindtime/pages/lists/Lists';
import MindtimeMyBackups from './mindtime/pages/my-backups/MyBackups';
import MindtimePrices from './mindtime/pages/prices/Prices';
import MindtimeSupport from './mindtime/pages/support/Support';
import MindtimeUsers from './mindtime/pages/users/Users';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import SecurityLayout from './mindtime/layout/SecurityLayout';
import ComplianceSettings from './mindtime/pages/security/compliance-settings/ComplianceSettings';
import AhsayPortal from './mindtime/pages/ahsay-portal/AhsayPortal';
import SecurityDashboard from './mindtime/pages/security/edr/Dashboard';
import SecurityOverview from './mindtime/pages/security/edr/Overview';
import SecuritySectionPlaceholder from './mindtime/pages/security/edr/SectionPlaceholder';
import SecurityClients from './mindtime/pages/security/edr/clients';
import ClientDetails from './mindtime/pages/security/edr/clients/ClientDetails';
import ConfigurationCompliance from './mindtime/pages/security/edr/configuration-compliance/ConfigurationCompliance';
import DeployNewAgent from './mindtime/pages/security/edr/deploy-agent/DeployNewAgent';
import FileIntegrityMonitoring from './mindtime/pages/security/edr/file-integrity-monitoring/FileIntegrityMonitoring';
import MalwareDetection from './mindtime/pages/security/edr/malware-detection/MalwareDetection';
import ThreatHunting from './mindtime/pages/security/edr/threat-hunting/ThreatHunting';
// eslint-disable-next-line import/extensions
// import FRSDetails from './components/organization/projects/frs/FRSDetails'; // MTB: /projects routes disabled
import useAuth from './hooks/useAuth';
import SecurityIndexRedirect from './mindtime/config/SecurityIndexRedirect';
import SecuritySubscriptionRedirect from './mindtime/config/SecuritySubscriptionRedirect';
import { SecurityLegacyEndpointRedirect, LegacyWazuPathRedirect } from './mindtime/config/securityLegacyRedirects';
import { SECURITY_LEGACY_ROUTE_REDIRECTS } from './mindtime/config/securityNav';
import {
  SECURITY_PAGE_ROUTES,
  SECURITY_SUB_ROUTE_PERMISSIONS,
  getSecurityPlaceholderNavItems,
  toSecurityChildPath
} from './mindtime/config/securityRoutes';
import { modulePermission } from './mindtime/config/subscriptionPermissionRegistry';
import { EDR_SOLUTION_ID } from './mindtime/constants/securitySolutions';
import SubOrgDashboard from './mindtime/pages/security/edr/SubOrgDashboard';
import AutomatedResponse from './mindtime/pages/security/edr/automated-response/AutomatedResponse';
import Endpoints from './mindtime/pages/security/edr/endpoints/Endpoints';
import EndpointDetails from './mindtime/pages/security/edr/endpoints/details/EndpointDetails';
import MitreAttck from './mindtime/pages/security/edr/mitre-attck/MitreAttck';
import VulnerabilityDetail from './mindtime/pages/security/edr/vulnerability-detection/VulnerabilityDetail';
import VulnerabilityDetection from './mindtime/pages/security/edr/vulnerability-detection/VulnerabilityDetection';
import { getAuthenticatedLandingPath } from './utils/permissionLanding';

const Loadable = (Component) => (props) =>
(
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);

const CreateCasesWizard = Loadable(
  lazy(() => import('./components/cases/CreateCasesWizard'))
);
const CreateOrganizationWizard = Loadable(
  lazy(() => import('./components/organization/CreateOrganizationWizard'))
);
const EditContacts = Loadable(lazy(() => import('./pages/contacts/edit')));
const Login = Loadable(lazy(() => import('./pages/authentication/Login')));
const PasswordRecovery = Loadable(
  lazy(() => import('./pages/authentication/PasswordRecovery'))
);
const PasswordReset = Loadable(
  lazy(() => import('./pages/authentication/PasswordReset'))
);
const AuthorizationRequired = Loadable(
  lazy(() => import('./pages/AuthorizationRequired'))
);
const NotFound = Loadable(lazy(() => import('./pages/NotFound')));
const ServerError = Loadable(lazy(() => import('./pages/ServerError')));
const ComplianceControlDetail = Loadable(
  lazy(() => import('./mindtime/pages/security/edr/configuration-compliance/ComplianceControlDetail'))
);
const FileIntegrityEventDetail = Loadable(
  lazy(() => import('./mindtime/pages/security/edr/file-integrity-monitoring/FileIntegrityEventDetail'))
);
const MalwareDetectionDetail = Loadable(
  lazy(() => import('./mindtime/pages/security/edr/malware-detection/MalwareDetectionDetail'))
);
const MitreAttckDetail = Loadable(
  lazy(() => import('./mindtime/pages/security/edr/mitre-attck/MitreAttckDetail'))
);
const ThreatHuntingDetail = Loadable(
  lazy(() => import('./mindtime/pages/security/edr/threat-hunting/ThreatHuntingDetail'))
);
const AutomatedResponseDetail = Loadable(
  lazy(() => import('./mindtime/pages/security/edr/automated-response/AutomatedResponseDetail'))
);

const PermissionAwareIndexRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={getAuthenticatedLandingPath(user)} replace />;
};

const ContactsToUsersEditRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/users/edit/${id}`} replace />;
};

const securityChildRoutes = getSecurityPlaceholderNavItems().map((item) => ({
  path: toSecurityChildPath(item.path),
  element: <SecuritySectionPlaceholder title={item.label} />,
}));

const securityLegacyRedirectRoutes = [
  ...SECURITY_LEGACY_ROUTE_REDIRECTS.filter(({ from }) => from !== 'overview').map(({ from, to }) => ({
    path: from,
    element: <Navigate to={`/mindtime/security/${to}`} replace />,
  })),
  {
    path: 'overview',
    element: <SecuritySubscriptionRedirect />,
  },
  {
    path: 'edr/overview',
    element: <SecuritySubscriptionRedirect />,
  },
  {
    path: 'endpoints/:systemWazuhAgentId',
    element: <SecurityLegacyEndpointRedirect />,
  },
];

// eslint-disable-next-line import/no-mutable-exports
let routes: any[] = [];

routes = [
  {
    path: '/',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      // ---> Dashboard
      {
        index: true,
        element: <PermissionAwareIndexRedirect />,
      },
      {
        path: 'dashboard',
        element: (
          <PermissionGuard hasPermission={CLIENT_DASHBOARD}>
            <Navigate to="/cases/dashboard" replace />
          </PermissionGuard>
        ),
      },
      {
        path: 'dashboard/ssl-overview',
        element: (
          <PermissionGuard hasPermission={CLIENT_DASHBOARD}>
            <UnderDevelopment title="SSL Overview" />
          </PermissionGuard>
        ),
      },
      {
        path: 'dashboard/backup-overview',
        element: (
          <PermissionGuard hasPermission={CLIENT_DASHBOARD}>
            <UnderDevelopment title="Backup Overview" />
          </PermissionGuard>
        ),
      },
      {
        path: 'dashboard/dynamic',
        element: (
          <PermissionGuard hasPermission={CLIENT_DASHBOARD}>
            <UnderDevelopment title="Dynamic Dashboard" />
          </PermissionGuard>
        ),
      },
      {
        path: 'backup/overview',
        element: (
          <PermissionGuard hasPermission={CLIENT_BACKUPS}>
            <UnderDevelopment title="Backup Overview" />
          </PermissionGuard>
        ),
      },
      {
        path: 'security/ssl',
        element: (
          <PermissionGuard hasPermission={CLIENT_MINDTIME_SECURITY_SSL_MANAGEMENT}>
            <UnderDevelopment title="SSL" />
          </PermissionGuard>
        ),
      },
      {
        path: 'administration/roles-permissions',
        element: (
          <PermissionGuard hasPermission={CLIENT_USERS}>
            <UnderDevelopment title="Roles & Permissions" />
          </PermissionGuard>
        ),
      },
      {
        path: 'administration/audit-log',
        element: (
          <PermissionGuard hasPermission={CLIENT_USERS}>
            <UnderDevelopment title="Audit Log" />
          </PermissionGuard>
        ),
      },
      // ---> Clients || Companies || Users
      {
        path: 'clients',
        element: (
          <PermissionGuard hasPermission={CLIETN_COMPANIES}>
            <Companies />
          </PermissionGuard>
        ),
      },
      {
        path: 'clients/:companyId/projects/:projectID',
        element: (
          <PermissionGuard hasPermission={CLIETN_COMPANIES}>
            <ProjectDetails />
          </PermissionGuard>
        ),
      },
      {
        path: 'clients/:companyId/:tab',
        element: (
          <PermissionGuard hasPermission={CLIETN_COMPANIES}>
            <CompanyDetails />
          </PermissionGuard>
        ),
      },
      {
        path: 'clients/:companyId',
        element: (
          <PermissionGuard hasPermission={CLIETN_COMPANIES}>
            <CompanyDetails />
          </PermissionGuard>
        ),
      },

      {
        path: 'users',
        element: (
          <PermissionGuard hasPermission={CLIENT_USERS}>
            <ContactsListView />
          </PermissionGuard>
        ),
      },
      {
        path: 'users/edit/:id',
        element: (
          <PermissionGuard hasPermission={CLIENT_USERS}>
            <EditContacts />
          </PermissionGuard>
        ),
      },
      {
        path: 'contacts',
        element: <Navigate to="/users" replace />,
      },
      {
        path: 'contacts/edit/:id',
        element: <ContactsToUsersEditRedirect />,
      },
      // ---> Account
      {
        path: 'account',
        element: <Navigate to="/user-settings" replace />,
      },
      {
        path: 'org-settings',
        element: <Navigate to="/user-settings?tab=company" replace />,
      },
      {
        path: 'user-settings',
        element: <UserSettings />,
      },
      {
        path: 'chats-history',
        element: <ChatsHistory />,
      },
      // ---> Projects (MTB: disabled — sidebar already hidden; do not register routes so /projects is unreachable)
      // {
      //   path: 'projects',
      //   element: (
      //     <PermissionGuard hasPermission={CLIENT_PROJECTS}>
      //       <ProjectSection />
      //     </PermissionGuard>
      //   ),
      // },
      // {
      //   path: 'projects/:projectID',
      //   element: (
      //     <PermissionGuard hasPermission={CLIENT_PROJECTS}>
      //       <OrganizationProjectDetails />
      //     </PermissionGuard>
      //   ),
      //   children: [
      //     { index: true, element: <Navigate to="cases" replace /> },
      //     { path: 'cases', element: <CasesViewByProjects /> },
      //     { path: 'frs', element: <ProjectFrs /> },
      //     {
      //       path: 'frs/:frsID/:tab',
      //       element: <FRSDetails />,
      //     },
      //     {
      //       path: 'frs/:frsID',
      //       element: <FRSDetails />,
      //     },
      //   ],
      // },
      // {
      //   path: 'projects/:projectID/all',
      //   element: (
      //     <PermissionGuard hasPermission={CLIENT_PROJECTS}>
      //       <ViewCases />
      //     </PermissionGuard>
      //   ),
      // },
      // {
      //   path: 'projects/:projectID/open',
      //   element: (
      //     <PermissionGuard hasPermission={CLIENT_PROJECTS}>
      //       <OpenCases />
      //     </PermissionGuard>
      //   ),
      // },
      // {
      //   path: 'projects/:projectID/approvals',
      //   element: (
      //     <PermissionGuard hasPermission={APPROVALS}>
      //       <UnApprovedCases />
      //     </PermissionGuard>
      //   ),
      // },
      // {
      //   path: 'projects/:projectID/backlog',
      //   element: (
      //     <PermissionGuard hasPermission={CLIENT_PROJECTS}>
      //       <BackLogCases />
      //     </PermissionGuard>
      //   ),
      // },
      // {
      //   path: 'projects/:projectID/testing',
      //   element: (
      //     <PermissionGuard hasPermission={CLIENT_PROJECTS}>
      //       <TestingCases />
      //     </PermissionGuard>
      //   ),
      // },
      // ---> Billable Reportings
      {
        path: 'reporting',
        element: (
          <PermissionGuard hasPermission={CLIENT_REPORTING}>
            <ViewReporting />
          </PermissionGuard>
        ),
      },
      {
        path: 'planning',
        element: (
          <PermissionGuard hasPermission={CLIENT_PLANNING}>
            <Planning />
          </PermissionGuard>
        ),
      },
      // ---> Subscriptions
      {
        path: 'subscriptions',
        element: (
          <PermissionGuard hasPermission={CLIENT_SUBSCRIPTIONS}>
            <Subscriptions />
          </PermissionGuard>
        ),
      },
      {
        path: 'subscriptions/:subscriptionId/:tab',
        element: (
          <PermissionGuard hasPermission={CLIENT_SUBSCRIPTIONS}>
            <SubscriptionDetails />
          </PermissionGuard>
        ),
      },
      {
        path: 'subscriptions/:subscriptionId',
        element: (
          <PermissionGuard hasPermission={CLIENT_SUBSCRIPTIONS}>
            <SubscriptionDetails />
          </PermissionGuard>
        ),
      },
      {
        path: 'create-subscription/:tab',
        element: (
          <PermissionGuard hasPermission={CLIENT_SUBSCRIPTIONS}>
            <CreateSubscription />
          </PermissionGuard>
        ),
      },
      {
        path: 'create-subscription',
        element: (
          <PermissionGuard hasPermission={CLIENT_SUBSCRIPTIONS}>
            <CreateSubscription />
          </PermissionGuard>
        ),
      },
      // ---> Orders
      {
        path: 'orders',
        element: (
          <PermissionGuard hasPermission={CLIENT_ORDERS}>
            <Orders />
          </PermissionGuard>
        ),
      },
      {
        path: 'create-order',
        element: (
          <PermissionGuard hasPermission={CLIENT_ORDERS}>
            <CreateOrder />
          </PermissionGuard>
        ),
      },
      {
        path: 'orders/:orderId',
        element: (
          <PermissionGuard hasPermission={CLIENT_ORDERS}>
            <ViewOrder />
          </PermissionGuard>
        ),
      },
      // ---> Cases
      {
        path: 'cases/all',
        element: (
          <PermissionGuard hasPermission={CLIENT_CASES}>
            <ViewCases />
          </PermissionGuard>
        ),
      },
      {
        path: 'cases/approvals',
        element: (
          <PermissionGuard hasPermission={APPROVALS}>
            <UnApprovedCases />
          </PermissionGuard>
        ),
      },
      {
        path: 'cases/backlog',
        element: (
          <PermissionGuard hasPermission={CLIENT_CASES}>
            <BackLogCases />
          </PermissionGuard>
        ),
      },
      {
        path: 'cases/testing',
        element: (
          <PermissionGuard hasPermission={CLIENT_CASES}>
            <TestingCases />
          </PermissionGuard>
        ),
      },
      {
        path: 'cases/open',
        element: (
          <PermissionGuard hasPermission={CLIENT_CASES}>
            <OpenCases />
          </PermissionGuard>
        ),
      },
      {
        path: 'cases/dashboard',
        element: (
          <PermissionGuard hasPermission={CLIENT_DASHBOARD}>
            <Overview />
          </PermissionGuard>
        ),
      },

      {
        path: 'cases/add',
        element: (
          <PermissionGuard hasPermission={CLIENT_CASES}>
            <CreateCasesWizard />
          </PermissionGuard>
        ),
      },
      {
        path: 'cases/:caseID',
        element: (
          <PermissionGuard hasPermission={CLIENT_CASES}>
            <EditCases />
          </PermissionGuard>
        ),
      },
      // ###############################################################################
      // ─── MindTime Routes ───
      // ###############################################################################
      {
        path: 'mindtime/home',
        element: (
          <PermissionGuard hasPermission={CLIETN_HOME}>
            <MindtimeDashboard />
          </PermissionGuard>
        ),
      },
      {
        path: 'mindtime/my-backups',
        element: (
          <PermissionGuard hasPermission={CLIENT_BACKUPS}>
            <MindtimeMyBackups />
          </PermissionGuard>
        ),
      },
      {
        path: 'mindtime/users',
        element: (
          <PermissionGuard hasPermission={CLIENT_BACKUP_USERS}>
            <MindtimeUsers />
          </PermissionGuard>
        ),
      },
      {
        path: 'mindtime/users/backups',
        element: (
          <PermissionGuard hasPermission={CLIENT_BACKUP_USERS}>
            <MindtimeMyBackups />
          </PermissionGuard>
        ),
      },
      {
        path: 'mindtime/prices',
        element: (
          <PermissionGuard hasPermission={CLIENT_PRICES}>
            <MindtimePrices />
          </PermissionGuard>
        ),
      },
      {
        path: 'mindtime/lists',
        element: (
          <PermissionGuard hasPermission={CLIENT_LISTS}>
            <MindtimeLists />
          </PermissionGuard>
        ),
      },
      {
        path: 'mindtime/history',
        element: (
          <PermissionGuard hasPermission={CLIENT_HISTORY}>
            <MindtimeHistory />
          </PermissionGuard>
        ),
      },
      {
        path: 'mindtime/support/service-status',
        element: (
          <PermissionGuard anyOfPermissions={[CLIENT_SUPPORT, CLIENT_EXPLORER]}>
            <MindtimeSupport />
          </PermissionGuard>
        ),
      },
      {
        path: 'mindtime/support/downloads',
        element: (
          <PermissionGuard anyOfPermissions={[CLIENT_SUPPORT, CLIENT_EXPLORER]}>
            <MindtimeSupport />
          </PermissionGuard>
        ),
      },
      {
        path: 'mindtime/support/knowledge-base/*',
        element: (
          <PermissionGuard anyOfPermissions={[CLIENT_SUPPORT, CLIENT_EXPLORER]}>
            <MindtimeSupport />
          </PermissionGuard>
        ),
      },
      {
        path: 'mindtime/support',
        element: (
          <PermissionGuard anyOfPermissions={[CLIENT_SUPPORT, CLIENT_EXPLORER]}>
            <MindtimeSupport />
          </PermissionGuard>
        ),
      },
      {
        path: 'mindtime/backup-portal',
        element: (
          <PermissionGuard hasPermission={CLIENT_BACKUP_PORTAL}>
            <AhsayPortal />
          </PermissionGuard>
        ),
      },
      {
        path: 'mindtime/ahsay-portal',
        element: <Navigate to="/mindtime/backup-portal" replace />,
      },
      // ###############################################################################
      // ─── Security Routes ───
      // ###############################################################################

      {
        path: 'mindtime/wazu/*',
        element: <LegacyWazuPathRedirect />,
      },
      {
        path: 'mindtime/security/compliance-settings',
        element: (
          <PermissionGuard hasPermission={CLIENT_MINDTIME_SECURITY_COMPLIANCE_SETTINGS}>
            <ComplianceSettings />
          </PermissionGuard>
        ),
      },
      {
        path: 'mindtime/security',
        element: <SecurityLayout />,
        children: [
          {
            index: true,
            element: <SecurityIndexRedirect />,
          },
          {
            path: SECURITY_PAGE_ROUTES.securityOperationsCenter,
            element: (
              <SubscriptionProductPermissionGuard hasPermission={modulePermission(EDR_SOLUTION_ID, 'security-operations-center')}>
                <SecurityDashboard />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.resellerDashboard,
            element: (
              <SubscriptionProductPermissionGuard hasPermission={modulePermission(EDR_SOLUTION_ID, 'reseller-dashboard')}>
                <SecurityOverview />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.subOrgDashboard,
            element: (
              <SubscriptionProductPermissionGuard hasPermission={modulePermission(EDR_SOLUTION_ID, 'sub-org-dashboard')}>
                <SubOrgDashboard />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.clientDetails,
            element: (
              <SubscriptionProductPermissionGuard
                hasPermission={SECURITY_SUB_ROUTE_PERMISSIONS.clientDetails}
                unavailableKey="unavailable.clientDetails"
              >
                <ClientDetails />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.clients,
            element: (
              <SubscriptionProductPermissionGuard hasPermission={modulePermission(EDR_SOLUTION_ID, 'clients')}>
                <SecurityClients />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.endpoints,
            element: (
              <SubscriptionProductPermissionGuard hasPermission={modulePermission(EDR_SOLUTION_ID, 'endpoints')}>
                <Endpoints />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.deployAgent,
            element: (
              <SubscriptionProductPermissionGuard hasPermission={SECURITY_SUB_ROUTE_PERMISSIONS.deployAgent}>
                <DeployNewAgent />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.endpointDetails,
            element: (
              <SubscriptionProductPermissionGuard
                hasPermission={modulePermission(EDR_SOLUTION_ID, 'endpoints')}
                unavailableKey="unavailable.endpointDetails"
              >
                <EndpointDetails />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.complianceDetail,
            element: (
              <SubscriptionProductPermissionGuard
                hasPermission={modulePermission(EDR_SOLUTION_ID, 'configuration-compliance')}
                unavailableKey="unavailable.complianceDetail"
              >
                <ComplianceControlDetail />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.configurationCompliance,
            element: (
              <SubscriptionProductPermissionGuard hasPermission={modulePermission(EDR_SOLUTION_ID, 'configuration-compliance')}>
                <ConfigurationCompliance />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.fileIntegrityDetail,
            element: (
              <SubscriptionProductPermissionGuard
                hasPermission={modulePermission(EDR_SOLUTION_ID, 'file-integrity-monitoring')}
                unavailableKey="unavailable.fileIntegrityDetail"
              >
                <FileIntegrityEventDetail />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.fileIntegrityMonitoring,
            element: (
              <SubscriptionProductPermissionGuard hasPermission={modulePermission(EDR_SOLUTION_ID, 'file-integrity-monitoring')}>
                <FileIntegrityMonitoring />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.threatHuntingDetail,
            element: (
              <SubscriptionProductPermissionGuard
                hasPermission={SECURITY_SUB_ROUTE_PERMISSIONS.threatHuntingDetail}
                unavailableKey="unavailable.threatHuntingDetail"
              >
                <ThreatHuntingDetail />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.threatHunting,
            element: (
              <SubscriptionProductPermissionGuard hasPermission={modulePermission(EDR_SOLUTION_ID, 'threat-hunting')}>
                <ThreatHunting />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.malwareDetail,
            element: (
              <SubscriptionProductPermissionGuard
                hasPermission={SECURITY_SUB_ROUTE_PERMISSIONS.malwareDetail}
                unavailableKey="unavailable.malwareDetail"
              >
                <MalwareDetectionDetail />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.malwareDetection,
            element: (
              <SubscriptionProductPermissionGuard hasPermission={modulePermission(EDR_SOLUTION_ID, 'malware-detection')}>
                <MalwareDetection />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.automatedResponseDetail,
            element: (
              <SubscriptionProductPermissionGuard
                hasPermission={SECURITY_SUB_ROUTE_PERMISSIONS.automatedResponseDetail}
                unavailableKey="unavailable.automatedResponse"
              >
                <AutomatedResponseDetail />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.automatedResponse,
            element: (
              <SubscriptionProductPermissionGuard hasPermission={modulePermission(EDR_SOLUTION_ID, 'automated-response')}>
                <AutomatedResponse />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.vulnerabilityDetection,
            element: (
              <SubscriptionProductPermissionGuard hasPermission={modulePermission(EDR_SOLUTION_ID, 'vulnerability-detection')}>
                <VulnerabilityDetection />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.vulnerabilityDetail,
            element: (
              <SubscriptionProductPermissionGuard
                hasPermission={modulePermission(EDR_SOLUTION_ID, 'vulnerability-detection')}
                unavailableKey="unavailable.vulnerabilityDetail"
              >
                <VulnerabilityDetail />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.mitreDetail,
            element: (
              <SubscriptionProductPermissionGuard
                hasPermission={SECURITY_SUB_ROUTE_PERMISSIONS.mitreDetail}
                unavailableKey="unavailable.mitreDetail"
              >
                <MitreAttckDetail />
              </SubscriptionProductPermissionGuard>
            ),
          },
          {
            path: SECURITY_PAGE_ROUTES.mitreAttck,
            element: (
              <SubscriptionProductPermissionGuard hasPermission={modulePermission(EDR_SOLUTION_ID, 'mitre-attck')}>
                <MitreAttck />
              </SubscriptionProductPermissionGuard>
            ),
          },
          ...securityChildRoutes,
          ...securityLegacyRedirectRoutes,
        ],
      },
    ],
  },

  {
    path: 'organizations/create',
    element: (
      <AuthGuard>
        <CreateOrganizationWizard />
      </AuthGuard>
    ),
  },
  {
    path: 'help',
    element: (
      <AuthGuard>
        <HelpLayout />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <Introduction />,
      },
      {
        path: 'dashboard',
        element: <Dashboard />,
      },

      {
        path: 'project',
        element: <Project />,
      },
      {
        path: 'contacts',
        element: <Contacts />,
      },
      {
        path: 'cases',
        children: [
          {
            index: true,
            element: <HelpCases />,
          },
          {
            path: 'create',
            element: <HelpCreateCases />,
          },
          {
            path: 'view',
            element: <HelpViewCases />,
          },
        ],
      },
    ],
  },

  // ---> Others
  {
    path: '*',
    children: [
      {
        path: 'login',
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ),
      },
      {
        path: 'login/two-factor-authentication',
        element: (
          <TwoFactorAuthGuard>
            <TwoFactorAuthentication />
          </TwoFactorAuthGuard>
        ),
      },
      {
        path: 'forgot-password',
        element: <PasswordRecovery />,
      },
      {
        path: 'reset-password',
        element: <PasswordReset />,
      },
      {
        path: '401',
        element: <AuthorizationRequired />,
      },
      {
        path: '404',
        element: <NotFound />,
      },
      {
        path: '500',
        element: <ServerError />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
];

export default routes;
